import React from "react";
import { useMantineTheme, Title, Stack } from "@mantine/core"; // Add Box and Title imports
import { LineChart, LineChartCurveType } from "@mantine/charts";
import TimeChartTooltip, { TimeChartTooltipProps } from "./TimeChartTooltip";
import { HorizontalReferenceLine, TimeStamp, VerticalReferenceLine } from "types/uiTypes";
import { useTranslation } from "react-i18next";

/**
 * Props for the `TimeLineChart` component.
 *
 * This interface defines the required and optional properties for rendering a time-based line chart.
 * It supports custom data, series configuration, tooltips, reference lines, and event handlers.
 */
export interface TimeLineChartProps {
  /**
   * The data points to be plotted on the chart.
   * Each object should contain a `time` (timestamp or number) and a `value` (number).
   * Example: [{ time: 1718000000, value: 42 }]
   */
  data: { time: TimeStamp | number; value: number; customTooltipProp?: TimeChartTooltipProps }[];

  /**
   * Array of series definitions for the chart.
   * Each series should specify a `color` (used for the line) and a `label` (displayed in the legend or tooltip).
   * Example: [{ color: "#1971c2", label: "Head Speed" }]
   */
  series: { color: string; label: string }[];

  /**
   * Props to customize the tooltip shown on hover.
   * Should match the `TimeChartTooltipProps` interface, allowing you to set title, labels, units, etc.
   */
  tooltipProps?: TimeChartTooltipProps;

  /**
   * Callback function triggered when a data point is hovered.
   * Receives the hovered timestamp as a percentage (0-100) of the total time range.
   * Useful for synchronizing external UI or highlighting related data.
   */
  onHovered: (percentage: number) => void;

  /**
   * Callback function triggered to remove focus or hover state from the chart.
   * Typically used to reset UI or clear highlights when the user stops hovering.
   */
  defocus: () => void;

  /**
   * Optional array of horizontal reference lines to display on the chart.
   * Each line can specify a value, label, and color.
   * Useful for marking thresholds or target values.
   */
  horizontalReferenceLines?: HorizontalReferenceLine[];

  /**
   * Optional array of vertical reference lines to display on the chart.
   * Each line can specify a time, label, and color.
   * Useful for marking events or milestones in the time series.
   */
  verticalReferenceLines?: VerticalReferenceLine[];

  /**
   * Optional title to display above the chart.
   * If provided, it will be rendered as a heading.
   */
  title?: string;

  curveType?: LineChartCurveType;
  withLegend?: boolean;
  withDots?: boolean;
  xAxisProps?: Record<string, unknown>;
  yAxisProps?: Record<string, unknown>;
}

const TimeLineChart: React.FC<TimeLineChartProps> = ({
  data,
  series,
  onHovered,
  defocus,
  tooltipProps,
  horizontalReferenceLines = [],
  verticalReferenceLines = [],
  title,
  curveType,
  withDots = false,
  withLegend,
  xAxisProps,
  yAxisProps,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => Number(a.time) - Number(b.time));
  }, [data]);

  const focusedTimestamp = (time: number) => {
    if (!onHovered) return;
    const lastTimestamp = sortedData[sortedData.length - 1].time;
    onHovered((time / lastTimestamp) * 100);
  };

  return (
    <Stack gap="xs">
      {title && (
        <Title order={6} ta="start" mb={theme.spacing.sm}>
          {title}
        </Title>
      )}
      <LineChart
        h={theme.spacing.chart}
        w="100%"
        data={sortedData}
        dataKey="time"
        series={series.map((s) => ({
          ...s,
          name: "value",
        }))}
        xAxisProps={{ type: "number", ...xAxisProps }}
        yAxisProps={yAxisProps}
        referenceLines={[
          ...horizontalReferenceLines.map((line) => ({
            ...line,
            color: line.color || "red",
            ifOverflow: "extendDomain" as const,
          })),
          ...verticalReferenceLines.map((line) => ({
            ...line,
            color: line.color || "red",
            ifOverflow: "extendDomain" as const,
            ...(line.labelPosition ? { labelPosition: line.labelPosition } : {}),
          })),
        ]}
        withTooltip={true}
        withDots={withDots}
        curveType={curveType || "natural"}
        strokeWidth={1}
        withLegend={withLegend ?? true}
        legendProps={{
          verticalAlign: "bottom",
          height: 30,
          formatter: (value) => t(value),
        }}
        tooltipProps={{
          content: (data) => {
            if (data.payload && data.payload?.length > 0) {
              const dataPoint = data.payload[0].payload;
              const pointTooltipProps = dataPoint.customTooltipProp || tooltipProps;
              // Removed unnecessary debug console.log statements.

              return (
                <TimeChartTooltip
                  title={pointTooltipProps?.title}
                  timeLabel={pointTooltipProps?.timeLabel}
                  timeStamp={dataPoint.time}
                  yLabel={pointTooltipProps?.yLabel}
                  yValueUnit={pointTooltipProps?.yValueUnit}
                  yValue={dataPoint["value"]}
                  timeUnit={pointTooltipProps?.timeUnit}
                  focusedTimestamp={focusedTimestamp}
                  defocus={defocus}
                />
              );
            }
          },
        }}
      />
    </Stack>
  );
};

export default TimeLineChart;
